import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Page, LayoutSingleColumn, ExternalLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { Accordion, AccordionItem } from '../../components/Accordion/Accordion';
import faqIcon from "../../assets/faq.png";

import css from './FAQPage.module.css';

export const FAQPageComponent = props => {
  const { scrollingDisabled, pageAssetsData, inProgress } = props;

  const title = 'Frequently Asked Questions';
  const description = 'Find answers to common questions about Rennzone Marketplace';

  // Get FAQ data from CMS
  const faqData = pageAssetsData?.faq?.data;
  const faqSections = faqData?.data?.sections || faqData?.sections || [];

  // Find the FAQ section from CMS data (article, faq, or accordion type)
  const faqAccordionSection = faqSections.find(section =>
    section.sectionType === 'article' ||
    section.sectionType === 'faq' ||
    section.sectionType === 'accordion'
  );

  // Get FAQ items (blocks) from the section and transform them
  const faqItems = (faqAccordionSection?.blocks || []).map(block => ({
    title: block.title?.content || block.title || '',
    text: block.text?.content || block.text || '',
  }));

  // Fallback FAQ items if no CMS data is available
    const defaultFaqItems = [
    {
      blockType: 'defaultBlock',
      title: 'How does Rennzone Marketplace work?',
      text: 'Rennzone is a trusted online marketplace dedicated to the sale and purchase of Porsche vehicles and related products. Sellers can easily list their items, while buyers have the opportunity to connect directly with sellers to ask questions and arrange purchases. The platform is designed to ensure a smooth, secure, and seamless experience for Porsche enthusiasts and collectors alike.',
    },
    {
      blockType: 'defaultBlock',
      title: 'Should I register an account with Rennzone?',
      text: 'To browse cars or items for sale, you do not need to be a member. If you wish to list and item for sale or contact a seller, you will need to register an account. Registering an account brings such benefits as keeping a watchlist of your favourites and receiving our newsletters, which are dedicated to enthusiasts and not trying to sell you something!',
    },
    {
      blockType: 'defaultBlock',
      title: 'I forgot my password. How can I reset it?',
      text: 'Go to the sign up / login page and click on reset my password. You will immediately receive a link to follow a simple process to reset your password.',
    },
    {
      blockType: 'defaultBlock',
      title: 'How do I list an item for sale on Rennzone?',
      text: 'Listing an item for sale on Rennzone is a very simple process. Firstly click on Sell in at the top of the website. You will then be prompted to sign up to create an account. Once you have done this, you can create your listing. This has been designed to be a very simple and quick process. In the unlikely event you have any issues, please contact our support for assistance. We have also created a guide on what to include in your listing to catch buyers attention, which can be read here.',
    },
    {
      blockType: 'defaultBlock',
      title: 'How do I know the Seller is trustworthy?',
      text: 'It is essential to be aware of potential red flags. At Rennzone, every listing undergoes a stringent approval process before it is listed live on our website. Any listing must meet specific requirements; if these are not met, the listing will be rejected, and we will contact the seller. Whilst we have a high degree of control over listings, we cannot provide complete protection to buyers; it is always best to take a cautious approach. Please read our Essential Guide to Safe Online Shopping here.',
    },
    {
      blockType: 'defaultBlock',
      title: 'When creating a listing, what should I use for my location?',
      text: 'Many listings are of high value, so it is crucial to conceal the exact location of the car or item for sale. When creating a listing, your location can be entered as wide as just selecting your country or as precise as your property number. The most commonly used is town and country, such as Stuttgart / Germany.',
    },
    {
      blockType: 'defaultBlock',
      title: 'How many photos should I use?',
      text: 'When uploading photos of your listing, the minimum requirement is one. However, we recommend using at least three images. There is no limit on Rennzone on how many photos you can upload. The maximum file size is 20mb, and the image needs to be either a .jpg or .png file. Please note that poor-quality photos are unlikely to engage potential buyers. You can add / remove pictures by editing your listing at any time.',
    },
    {
      blockType: 'defaultBlock',
      title: 'I can\'t find the model I wish to list?',
      text: 'We try our best to ensure all models are listed, however, if your model is not listed, please contact us below and we will ensure it is added quickly for you.',
    },
  ];

  // Use CMS data if available, otherwise use default items
  const displayFaqItems = faqItems.length > 0 ? faqItems : defaultFaqItems;

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.layoutWrapperMain}
      >
        <div className={css.root}>
          {/* Hero Section */}
          <div className={css.heroSection}>
            <div className={css.heroContent}>
              <img
                src={faqIcon}
                alt="FAQ Icon"
                className={css.heroIcon}
              />
              <p className={css.pageTitle}>FAQs - Frequently asked questions</p>
              <p className={css.pageSubtitle}>
                Find answers to common questions about listing, buying, managing your account, and more.
                If you can't find what you're looking for, feel free to contact our support team.
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className={css.faqSection}>
            {inProgress ? (
              <div className={css.loading}>Loading FAQs...</div>
            ) : (
              <Accordion>
                {displayFaqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    title={item.title}
                    children={item.text}
                  />
                ))}
              </Accordion>
            )}
          </div>

          {/* Contact Support Section */}
          <div className={css.contactSection}>
            <h2 className={css.contactTitle}>Still have questions?</h2>
            <h2 className={css.contactSubtitle}>Our customer support team is here to help you with any additional questions</h2>
            <ExternalLink href="mailto:support@rennzone.com" className={css.contactButton}>
              Contact Support
            </ExternalLink>
          </div>

          {/* Comprehensive Guide Section */}
          <div className={css.guideSection}>
            <div className={css.guideContainer}>
              {/* Header Section */}
              <section className={css.guideHeaderSection}>
                <h1 className={css.guideMainTitle}>
                  Your Comprehensive Guide to Rennzone: Frequently Asked Questions
                </h1>
                <p className={css.guideMainDescription}>
                  Navigating a specialized automotive platform can raise many questions, whether you
                  are a long-time Porsche enthusiast looking to sell a rare piece of engineering. Our
                  marketplace is built specifically for the Porsche community, offering a tailored
                  environment for road cars, race cars, and high-quality parts. Below we address some of
                  the most common queries to help you buy and sell with confidence.
                </p>
              </section>

              {/* Buying Section */}
              <section className={css.guideHeaderSection}>
                <h2 className={css.guideSectionTitle}>
                  Buying on Rennzone: What You Need to Know
                </h2>

                <div className={css.guideItemsGroup}>
                  <div>
                    <h3 className={css.guideItemTitle}>How do I find a specific Porsche model?</h3>
                    <p className={css.guideItemText}>
                      Our search functionality is designed to filter by the technical specifications that
                      matter most to collectors. Whether you are seeking an air-cooled 911 generations such
                      as air-cooled G-Series, 964, and 993 models—or modern water-cooled variants like the
                      997, 991, and latest 992. Additionally, you can filter for specialized variants like
                      the GT3, Turbo, or Targa models to narrow your hunt.
                    </p>
                  </div>

                  <div>
                    <h3 className={css.guideItemTitle}>Can I buy parts and memorabilia on the site?</h3>
                    <p className={css.guideItemText}>
                      Yes. Rennzone features a dedicated section for Porsche parts, wheels, and
                      accessories—from original factory parts for restoring a classic 356 or looking for BBS
                      motorsport alloys for a track-day build. Our marketplace connects you not just with vehicles
                      and private owners across the UK.
                    </p>
                  </div>

                  <div>
                    <h3 className={css.guideItemTitle}>What should I check before purchasing?</h3>
                    <p className={css.guideItemText}>
                      We recommend a thorough review of the vehicle's Full Porsche Service History (FSH),
                      particularly for high-performance variants like the 911 Turbo or GT3. For modern-era
                      water-cooled cars, it is vital to check for documentation regarding common mechanical
                      maintenance, such as IMS bearing replacements or bore scoring issues on early Boxster
                      and 996 models. We also suggest a Pre-Purchase Inspection (PPI) to verify the mechanical
                      health and paint depth of a potential acquisition.
                    </p>
                  </div>
                </div>
              </section>

              {/* Selling Section */}
              <section className={css.guideHeaderSection}>
                <h2 className={css.guideSectionTitle}>
                  Selling on Rennzone: Reaching the Right Audience
                </h2>

                <div className={css.guideItemsGroup}>
                  <div>
                    <h3 className={css.guideItemTitle}>How do I create an effective listing?</h3>
                    <p className={css.guideItemText}>
                      A successful sale begins with transparency. We recommend taking high-resolution photographs
                      that showcase the exterior, the interior (especially seat bolsters), the engine bay, and
                      undercarriage. Include a detailed description of your Porsche's history—one that highlights
                      recent major maintenance—such as a new clutch, brake discs, or a comprehensive engine
                      rebuild—can significantly build trust with potential buyers.
                    </p>
                  </div>

                  <div>
                    <h3 className={css.guideItemTitle}>Why has my listing not appeared yet?</h3>
                    <p className={css.guideItemText}>
                      Every marketplace often ask the technical fields required to describe a Porsche correctly.
                      By listing on Rennzone, your vehicle is seen by a high-intent audience of Porsche buyers.
                      We also ask you to understand the value of specific options, rare paint-to-sample (PTS){' '}
                      <span className={css.ptsColors}>
                        <span className={css.ptsRed}>&#x1F534;</span>
                        <span className={css.ptsOrange}>&#x1F7E0;</span>
                        <span className={css.ptsYellow}>&#x1F7E1;</span>
                        <span className={css.ptsGreen}>&#x1F7E2;</span>
                        <span className={css.ptsBlue}>&#x1F535;</span>
                      </span>{' '}
                      and/or performance-oriented specifications like the M030 package.
                    </p>
                  </div>
                </div>
              </section>

              {/* Community Section */}
              <section>
                <h2 className={css.guideSectionTitle}>
                  Community and Trust
                </h2>

                <div className={css.guideItemsGroup}>
                  <div>
                    <h3 className={css.guideItemTitle}>How does Rennzone support the UK Porsche scene?</h3>
                    <p className={css.guideItemText}>
                      Beyond being a marketplace, we aim to be a hub for the community. Our platform is integrated
                      with social media channels and forums as more that community brings together like-minded
                      enthusiasts for track days, meetups, and restoration showcases.
                    </p>
                  </div>

                  <div>
                    <h3 className={css.guideItemTitle}>What if I have more questions?</h3>
                    <p className={css.guideItemText}>
                      Our support team is deeply integrated into the Porsche world. If you have questions about a
                      specific listing, technical advice about a particular model, or need help understanding the
                      registration year of a German-supplied import, we are here to help.
                    </p>
                  </div>
                </div>

                <p className={css.guideFooterText}>
                  Visit our{' '}
                  <a href="/p/categories" className={css.guideLink}>
                    categories
                  </a>{' '}
                  to see the range of vehicles for sale or explore our{' '}
                  <a href="/s" className={css.guideLink}>
                    Search
                  </a>{' '}
                  page to start your journey today.
                </p>
              </section>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

FAQPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress } = state.hostedAssets || {};
  return {
    scrollingDisabled: state.ui.scrollingDisabled,
    pageAssetsData,
    inProgress,
  };
};

const FAQPage = compose(connect(mapStateToProps))(FAQPageComponent);

export default FAQPage;
